"use client"

import { useEffect, useState, useCallback } from "react"
import Image from "next/image"
import { X, ChevronLeft, ChevronRight } from "lucide-react"

export type SessionPhoto = {
  image: string
  alt: string
  caption: string
  order: number
}

const PER_PAGE = 4

type Props = {
  photos: SessionPhoto[]
}

/**
 * Session gallery carousel: shows PER_PAGE landscape tiles at a time with
 * left/right arrows. Clicking a tile opens a full-screen lightbox
 * (Esc / ← / → keyboard support, click backdrop to close).
 */
export function SessionGalleryLightbox({ photos }: Props) {
  const totalPages = Math.max(1, Math.ceil(photos.length / PER_PAGE))
  const [page, setPage] = useState(0)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const clampPage = (p: number) => Math.min(Math.max(p, 0), totalPages - 1)
  const nextPage = useCallback(() => setPage((p) => clampPage(p + 1)), [totalPages])
  const prevPage = useCallback(() => setPage((p) => clampPage(p - 1)), [totalPages])

  const close = useCallback(() => setOpenIndex(null), [])
  const nextPhoto = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i + 1) % photos.length)),
    [photos.length]
  )
  const prevPhoto = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  )

  // Reset page if the photo set shrinks below the current page.
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1)
  }, [totalPages, page])

  useEffect(() => {
    if (openIndex === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
      else if (e.key === "ArrowRight") nextPhoto()
      else if (e.key === "ArrowLeft") prevPhoto()
    }
    window.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [openIndex, close, nextPhoto, prevPhoto])

  const start = page * PER_PAGE
  const visible = photos.slice(start, start + PER_PAGE)
  const atStart = page === 0
  const atEnd = page === totalPages - 1

  return (
    <>
      <div className="flex items-center gap-2 md:gap-4">
        {/* Prev arrow */}
        <button
          type="button"
          onClick={prevPage}
          disabled={atStart}
          aria-label="Previous photos"
          className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full bg-card ring-1 ring-border/70 shadow-soft text-foreground hover:bg-primary hover:text-white hover:shadow-lifted disabled:opacity-30 disabled:hover:bg-card disabled:hover:text-foreground disabled:cursor-not-allowed transition-all flex items-center justify-center"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Carousel grid */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {visible.map((photo, i) => {
              const absoluteIndex = start + i
              return (
                <button
                  key={photo.image}
                  type="button"
                  onClick={() => setOpenIndex(absoluteIndex)}
                  aria-label={`View larger: ${photo.caption}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted ring-1 ring-border/60 shadow-soft text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Image
                    src={photo.image}
                    alt={photo.alt}
                    fill
                    sizes="(min-width: 768px) 18vw, 45vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading={absoluteIndex >= PER_PAGE ? "lazy" : undefined}
                  />
                </button>
              )
            })}
          </div>

          {/* Page indicators */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-label={`Go to page ${i + 1}`}
                  aria-current={i === page}
                  className={`h-2 rounded-full transition-all ${
                    i === page ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Next arrow */}
        <button
          type="button"
          onClick={nextPage}
          disabled={atEnd}
          aria-label="Next photos"
          className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full bg-card ring-1 ring-border/70 shadow-soft text-foreground hover:bg-primary hover:text-white hover:shadow-lifted disabled:opacity-30 disabled:hover:bg-card disabled:hover:text-foreground disabled:cursor-not-allowed transition-all flex items-center justify-center"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {openIndex !== null && photos[openIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  prevPhoto()
                }}
                aria-label="Previous photo"
                className="absolute left-2 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  nextPhoto()
                }}
                aria-label="Next photo"
                className="absolute right-2 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <figure
            className="relative w-full max-w-4xl flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-[4/3] max-h-[75vh]">
              <Image
                src={photos[openIndex].image}
                alt={photos[openIndex].alt}
                fill
                sizes="90vw"
                className="object-contain rounded-xl"
                priority
              />
            </div>
            <figcaption className="mt-4 text-center text-white/85 text-base max-w-xl">
              {photos[openIndex].caption}
            </figcaption>
          </figure>
        </div>
      )}
    </>
  )
}
